import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/BrandLogo';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
    Loader2, AlertTriangle, RefreshCw, Copy, Check, Zap, Sparkles,
    Users, FileSignature, FileClock, Receipt, TrendingUp, Target, Headset,
} from 'lucide-react';
import { API_BASE } from '../api';

const FEATURE_HIGHLIGHTS = [
    { icon: Users, label: 'Client CRM', desc: 'Every client, contact and status in one pipeline' },
    { icon: FileSignature, label: 'Proposals & E-Sign', desc: 'Send, track opens and get proposals signed online' },
    { icon: FileClock, label: 'Contracts & Renewals', desc: 'Auto-tracked terms with renewal alerts' },
    { icon: Receipt, label: 'Invoicing & Billing', desc: 'One-off and recurring invoices, paid or overdue' },
    { icon: TrendingUp, label: 'Profitability & Forecasting', desc: 'Per-client gross profit with a cost forecast' },
    { icon: Target, label: 'Lead Generation', desc: 'Discover, score and pipeline new leads' },
    { icon: Headset, label: 'Client Support Hub', desc: 'Tickets, priorities and SLAs, all in view' },
];

function MsIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
    );
}

const HUMAN_MS_ERRORS = {
    unknown_user: (email) => `Microsoft account ${email || ''} is not authorised for HQ. Use your registered admin email.`,
    exchange_failed: () => 'Microsoft accepted your sign-in but the token exchange failed. Most likely the Azure client secret has expired or doesn\'t match this deployment.',
    not_configured: () => 'Microsoft sign-in is not configured on this deployment. See the details below.',
    invalid_config: () => 'Microsoft sign-in is configured but Azure rejected the credentials — most likely the AZURE_TENANT_ID, AZURE_CLIENT_ID or AZURE_CLIENT_SECRET on this deployment contains a stale or placeholder value (check after redeploy).',
    invalid_client: () => 'Azure rejected the client credentials. The AZURE_CLIENT_ID or AZURE_CLIENT_SECRET on this deployment doesn\'t match the app registration.',
    redirect_uri_mismatch: () => 'Azure rejected the redirect URI. The URL configured on this deployment is not in the app registration\'s "Redirect URIs" list.',
};

export default function Login() {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const msError = params.get('ms_error');
    const msEmail = params.get('email');
    const missingFromUrl = (params.get('missing') || '').split(',').filter(Boolean);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [previewMode, setPreviewMode] = useState(null);   // {available, admin_email, host, is_production}
    const [previewLoggingIn, setPreviewLoggingIn] = useState(false);
    const [diag, setDiag] = useState(null);
    const [healthDiag, setHealthDiag] = useState(null);
    const [probing, setProbing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [demoLoggingIn, setDemoLoggingIn] = useState(false);

    // Surface a contextual error from the MS callback redirect.
    const initialError = (() => {
        if (!msError) return '';
        const human = HUMAN_MS_ERRORS[msError];
        if (human) return human(msEmail);
        return `Microsoft sign-in error: ${msError}`;
    })();
    const [error, setError] = useState(initialError);

    // When the MS callback bounces back with an ms_error — or the user hits
    // Sign in with Microsoft and it's not configured — auto-fetch the server
    // diagnostic so we can show admins exactly what to fix.
    useEffect(() => {
        if (!msError) return;
        let live = true;
        axios.get(`${API_BASE}/auth/microsoft/diagnostic`)
            .then(({ data }) => { if (live) setDiag(data); })
            .catch(() => { /* non-fatal */ });
        return () => { live = false; };
    }, [msError]);

    // Check whether this deployment is preview (not prod) — if so, show the
    // quick admin sign-in button so the user doesn't have to remember
    // credentials that change between forks.
    useEffect(() => {
        let live = true;
        axios.get(`${API_BASE}/auth/preview-mode`, { withCredentials: true })
            .then(({ data }) => { if (live) setPreviewMode(data); })
            .catch(() => { if (live) setPreviewMode({ available: false }); });
        return () => { live = false; };
    }, []);

    const quickPreviewLogin = async () => {
        setPreviewLoggingIn(true);
        setError('');
        try {
            await axios.post(`${API_BASE}/auth/preview-login`, {}, { withCredentials: true });
            // Force the AuthContext to pick up the new cookies
            window.location.assign('/dashboard');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(detail || 'Preview sign-in failed. Use email + password.');
        } finally {
            setPreviewLoggingIn(false);
        }
    };

    const tryDemo = async () => {
        setDemoLoggingIn(true);
        setError('');
        try {
            await axios.post(`${API_BASE}/auth/demo-login`, {}, { withCredentials: true });
            window.location.assign('/dashboard');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(detail || 'Could not start the demo right now — please try again.');
        } finally {
            setDemoLoggingIn(false);
        }
    };

    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;

    const probeDeployment = async () => {
        setProbing(true);
        try {
            const r = await axios.get(`${API_BASE}/health/deployment`);
            setHealthDiag(r.data);
        } catch (err) {
            // If we can't even reach the diagnostic, surface that directly
            setHealthDiag({
                _network_error: true,
                message: err?.message || 'Network error',
                hint: 'The backend is unreachable from this browser. Check that the backend service is running and that REACT_APP_BACKEND_URL on the frontend points to the right host.',
            });
        } finally { setProbing(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setHealthDiag(null);
        setSubmitting(true);
        const res = await login(email.trim(), password);
        setSubmitting(false);
        if (res.ok) navigate('/dashboard');
        else {
            setError(res.error || 'Login failed');
            // Auto-probe deployment health when any login attempt fails so the
            // admin doesn't have to guess what's broken on this redeploy.
            probeDeployment();
        }
    };

    const copy = (text) => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const showDiagnostic = !!msError;
    const missingVars = (diag?.missing_env_vars && diag.missing_env_vars.length) ? diag.missing_env_vars : missingFromUrl;

    return (
        <div className="min-h-screen w-full bm-login-bg flex flex-col items-center px-4 py-10" data-testid="login-page">
            {previewMode?.available && (
                <div
                    className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold tracking-wider uppercase bg-[color:var(--bm-accent)]/15 text-[color:var(--bm-accent-600)] border border-[color:var(--bm-accent)]/30 backdrop-blur-sm"
                    data-testid="preview-badge"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--bm-accent)] animate-pulse" />
                    Preview
                </div>
            )}

            {/* Top call-out — one-click read-only demo tour */}
            <div className="w-full max-w-[980px] mb-6" data-testid="try-demo-banner">
                <button
                    type="button"
                    onClick={tryDemo}
                    disabled={demoLoggingIn}
                    className="w-full flex items-center justify-center gap-2.5 h-[52px] rounded-xl text-[14px] font-semibold text-white transition disabled:opacity-70 shadow-[0_16px_40px_-16px_rgba(15,95,107,0.55)] hover:brightness-110"
                    style={{ background: 'linear-gradient(120deg, var(--bm-accent) 0%, #0F5F6B 100%)' }}
                    data-testid="try-demo-btn"
                >
                    {demoLoggingIn ? <Loader2 className="animate-spin" size={17} /> : <Sparkles size={17} />}
                    Try the Demo — explore every module, no sign-up needed
                </button>
                <div className="mt-2 text-center text-[11.5px] text-[color:var(--bm-text-subtle)]">
                    Read-only tour with sample data · nothing you click will change anything
                </div>
            </div>

            <div className="w-full max-w-[980px] grid md:grid-cols-[1.1fr_1fr] gap-8 items-stretch bm-fade-in">
                {/* Left — brand panel */}
                <div
                    className="hidden md:flex flex-col justify-between rounded-2xl p-10 text-white"
                    style={{
                        background: 'linear-gradient(150deg, #0B3C5D 0%, #072C44 60%, #0F5F6B 100%)',
                        boxShadow: '0 30px 80px -30px rgba(11, 60, 93, 0.45)',
                    }}
                >
                    <div>
                        <BrandLogo variant="icon" />
                        <h1 className="mt-8 font-display text-[30px] leading-[1.15] font-semibold tracking-tight">
                            Run your business
                            <br />
                            <span style={{ color: 'var(--bm-accent)' }}>beyond measure.</span>
                        </h1>
                        <p className="mt-4 text-white/70 text-[13.5px] leading-relaxed max-w-[400px]">
                            Your internal operating system for clients, proposals, contracts, renewals and revenue — in one premium workspace.
                        </p>
                        <div className="mt-7 space-y-3.5" data-testid="feature-highlights-list">
                            {FEATURE_HIGHLIGHTS.map(({ icon: Icon, label, desc }) => (
                                <div key={label} className="flex items-start gap-3">
                                    <div className="mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-white/10 border border-white/15">
                                        <Icon size={15} style={{ color: 'var(--bm-accent)' }} />
                                    </div>
                                    <div>
                                        <div className="text-[13px] font-semibold leading-tight">{label}</div>
                                        <div className="text-[11.5px] text-white/55 leading-snug">{desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="text-[11.5px] text-white/50 tracking-wide uppercase mt-8">
                        Beyond Measure HQ · Internal Access
                    </div>
                </div>

                {/* Right — login form */}
                <div className="bg-white rounded-2xl p-8 md:p-10 border border-[color:var(--bm-border)] shadow-sm flex flex-col justify-center">
                    <div className="md:hidden mb-6 flex justify-center">
                        <BrandLogo variant="short" />
                    </div>
                    <div className="mb-7">
                        <div className="text-[11px] tracking-[0.15em] uppercase text-[color:var(--bm-accent-600)] font-semibold">
                            Welcome back
                        </div>
                        <h2 className="font-display text-[26px] font-semibold text-[color:var(--bm-text)] mt-2 leading-tight">
                            Sign in to Beyond Measure HQ
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-[12.5px] font-medium text-[color:var(--bm-text-muted)]">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@beyondmeasure.global"
                                className="mt-1.5 h-11"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-[12.5px] font-medium text-[color:var(--bm-text-muted)]">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 h-11"
                                data-testid="login-password-input"
                            />
                        </div>
                        {error && (
                            <div className="text-[12.5px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 mt-2 font-medium"
                            style={{ background: 'var(--bm-primary)' }}
                            data-testid="login-submit-btn"
                        >
                            {submitting ? <Loader2 className="animate-spin" size={16} /> : 'Sign in'}
                        </Button>
                    </form>

                    <div className="mt-5">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[color:var(--bm-border)]" /></div>
                            <div className="relative flex justify-center text-[11px] uppercase tracking-[0.12em]">
                                <span className="bg-white px-3 text-[color:var(--bm-text-subtle)]">or</span>
                            </div>
                        </div>
                        <a
                            href={`${API_BASE}/auth/microsoft/start`}
                            className="mt-4 flex items-center justify-center gap-3 h-11 w-full rounded-md border border-[color:var(--bm-border)] bg-white hover:bg-[color:var(--bm-bg-mute)] transition text-[13.5px] font-medium text-[color:var(--bm-text)]"
                            data-testid="login-ms-btn"
                        >
                            <MsIcon />
                            Sign in with Microsoft
                        </a>
                    </div>

                    {previewMode?.available && (
                        <div className="mt-3" data-testid="preview-login-section">
                            <button
                                type="button"
                                onClick={quickPreviewLogin}
                                disabled={previewLoggingIn}
                                className="flex items-center justify-center gap-2 h-11 w-full rounded-md text-[13.5px] font-medium text-[color:var(--bm-accent-600)] border border-dashed border-[color:var(--bm-accent)]/40 bg-[color:var(--bm-accent)]/5 hover:bg-[color:var(--bm-accent)]/10 transition disabled:opacity-60"
                                data-testid="preview-login-btn"
                            >
                                {previewLoggingIn
                                    ? <Loader2 className="animate-spin" size={16} />
                                    : <Zap size={15} />}
                                Quick admin sign-in
                                <span className="text-[11px] font-normal text-[color:var(--bm-text-subtle)]">
                                    ({previewMode.admin_email})
                                </span>
                            </button>
                            <div className="mt-1.5 text-[10.5px] text-center text-[color:var(--bm-text-subtle)]">
                                Preview only — disabled on production.{' '}
                                <span className="font-mono">{previewMode.host}</span>
                            </div>
                        </div>
                    )}

                    {/* Self-help diagnostic — only shows after an MS sign-in failure */}
                    {(showDiagnostic || msError) && (
                        <div
                            className="mt-5 rounded-lg border border-amber-200 bg-amber-50/70 p-4 text-[12.5px] text-amber-900"
                            data-testid="login-ms-diagnostic"
                        >
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-700" />
                                <div className="flex-1 space-y-2">
                                    <div className="font-semibold text-amber-900">
                                        Microsoft sign-in isn't working on this deployment.
                                    </div>
                                    {missingVars.length > 0 && (
                                        <div>
                                            <div className="mt-1">Missing or empty environment variables in this backend:</div>
                                            <ul className="mt-1 ml-4 list-disc font-mono text-[11.5px]">
                                                {missingVars.map(v => <li key={v}>{v}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {diag?.redirect_uri && (
                                        <div>
                                            <div className="font-medium">Redirect URI configured on this server:</div>
                                            <div className="mt-1 flex items-center gap-2 font-mono text-[11.5px] bg-white rounded border border-amber-200 px-2 py-1.5 break-all">
                                                <span className="flex-1">{diag.redirect_uri}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => copy(diag.redirect_uri)}
                                                    className="text-amber-700 hover:text-amber-900"
                                                    aria-label="Copy redirect URI"
                                                    data-testid="login-ms-copy-redirect"
                                                >
                                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                                </button>
                                            </div>
                                            <div className="mt-1 text-[11.5px]">
                                                Must be listed <em>exactly</em> under your Azure AD app registration → <strong>Authentication → Redirect URIs (Web)</strong>.
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-[11.5px] leading-relaxed pt-1 border-t border-amber-200">
                                        <strong>How to fix:</strong> sign in with email + password (above), open <strong>Settings → Microsoft 365 connection</strong> for full details. After updating env vars on the deployment, restart the backend.{' '}
                                        <button
                                            type="button"
                                            onClick={() => window.location.reload()}
                                            className="inline-flex items-center gap-1 underline font-medium hover:text-amber-700"
                                            data-testid="login-ms-retry-btn"
                                        >
                                            <RefreshCw size={11} /> Retry
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <DeploymentDiagnosticPanel diag={healthDiag} onProbe={probeDeployment} probing={probing} />

                    <div className="mt-8 text-[11.5px] text-[color:var(--bm-text-subtle)] leading-relaxed">
                        Internal system for Beyond Measure team only.
                    </div>
                </div>
            </div>
        </div>
    );
}

function DeploymentDiagnosticPanel({ diag, onProbe, probing }) {
    if (!diag) {
        return (
            <div className="mt-5 text-[11.5px] text-center text-[color:var(--bm-text-subtle)]">
                Login failing on this deployment?{' '}
                <button
                    type="button"
                    onClick={onProbe}
                    disabled={probing}
                    className="underline font-medium hover:text-[color:var(--bm-text)]"
                    data-testid="login-deployment-diag-btn"
                >
                    {probing ? 'Checking…' : 'Diagnose deployment'}
                </button>
            </div>
        );
    }
    if (diag._network_error) {
        return (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-[12.5px] text-red-900" data-testid="login-deployment-diag">
                <div className="font-semibold mb-1">Backend unreachable</div>
                <div>{diag.message}</div>
                <div className="text-[11.5px] mt-1.5 text-red-800">{diag.hint}</div>
            </div>
        );
    }
    const critical = (diag.problems || []).filter(p => p.severity === 'critical');
    const warnings = (diag.problems || []).filter(p => p.severity === 'warning');
    const infos    = (diag.problems || []).filter(p => p.severity === 'info');
    const ok = diag.healthy;
    return (
        <div className={`mt-5 rounded-lg border p-4 text-[12.5px] ${ok ? 'border-emerald-200 bg-emerald-50/70 text-emerald-900' : 'border-red-200 bg-red-50 text-red-900'}`} data-testid="login-deployment-diag">
            <div className="flex items-start justify-between gap-2">
                <div className="font-semibold">
                    {ok ? '✓ Deployment looks healthy' : '✗ Deployment configuration problems detected'}
                </div>
                <button type="button" onClick={onProbe} className="text-[11px] underline" data-testid="login-deployment-rediag-btn">
                    {probing ? 'Re-checking…' : 'Re-check'}
                </button>
            </div>
            {!ok && (
                <ul className="mt-2 space-y-2">
                    {critical.map(p => (
                        <li key={p.key} className="border-l-2 border-red-400 pl-2.5">
                            <div className="font-semibold">{p.message}</div>
                            <div className="text-[11.5px] mt-0.5 opacity-90">{p.hint}</div>
                        </li>
                    ))}
                </ul>
            )}
            {warnings.length > 0 && (
                <details className="mt-2">
                    <summary className="cursor-pointer text-[11.5px] font-medium">{warnings.length} warning{warnings.length === 1 ? '' : 's'}</summary>
                    <ul className="mt-2 space-y-1.5">
                        {warnings.map(p => (
                            <li key={p.key} className="text-[11.5px]"><strong>{p.message}</strong><div className="opacity-80">{p.hint}</div></li>
                        ))}
                    </ul>
                </details>
            )}
            {infos.length > 0 && (
                <details className="mt-1.5">
                    <summary className="cursor-pointer text-[11.5px] opacity-80">{infos.length} info note{infos.length === 1 ? '' : 's'}</summary>
                    <ul className="mt-2 space-y-1.5">
                        {infos.map(p => (
                            <li key={p.key} className="text-[11.5px]"><strong>{p.message}</strong><div className="opacity-80">{p.hint}</div></li>
                        ))}
                    </ul>
                </details>
            )}
            <details className="mt-2 text-[11px] opacity-80">
                <summary className="cursor-pointer">Show full status (safe — no secrets)</summary>
                <pre className="mt-2 max-h-64 overflow-auto rounded bg-white/60 border border-current/10 p-2 text-[10.5px] leading-relaxed">{JSON.stringify(diag, null, 2)}</pre>
            </details>
        </div>
    );
}
